/*
 * Content Grid interactions — scoped per [data-slider][data-slider-type="content-grid"] root.
 * Hooks: [data-slider], [data-slider-track], [data-slider-prev/next], [data-media-card].
 */

// Must stay in sync with Tailwind's lg: and md: breakpoints
const style = getComputedStyle(document.documentElement);
const DESKTOP_BREAKPOINT = parseInt(style.getPropertyValue('--breakpoint-lg'), 10) || 1024;
const TABLET_BREAKPOINT = parseInt(style.getPropertyValue('--breakpoint-md'), 10) || 768;

class ContentGrid {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-slider-track]');
    this.controls = root.querySelector('[data-slider-controls]');
    this.prevBtn = root.querySelector('[data-slider-prev]');
    this.nextBtn = root.querySelector('[data-slider-next]');
    this.slideCount = parseInt(root.dataset.slideCount, 10) || 0;
    this.desktopVisible = parseInt(root.dataset.sliderDesktopVisible, 10) || 3;

    this.currentIndex = 0;
    this.itemsVisible = 1;
    this.initialOffset = 0;

    if (!this.track || this.slideCount === 0) return;

    this.init();
  }

  init() {
    this.updateVisibility();
    this.updateControlsVisibility();
    this.calculateInitialOffset();
    this.bindEvents();
    this.updateState();
  }

  calculateInitialOffset() {
    // Capture the initial left position of the first card to account for track padding
    const firstChild = this.track.querySelector('[data-media-card]');
    if (firstChild) {
      this.initialOffset = firstChild.offsetLeft;
    }
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => {
      this.goTo(this.currentIndex - 1);
    });

    this.nextBtn?.addEventListener('click', () => {
      this.goTo(this.currentIndex + 1);
    });

    // Handle mobile scroll sync
    let scrollTimer;
    this.track.addEventListener(
      'scroll',
      () => {
        if (window.innerWidth >= DESKTOP_BREAKPOINT) return;

        if (scrollTimer) cancelAnimationFrame(scrollTimer);
        scrollTimer = requestAnimationFrame(() => {
          const cardWidth = this.track.querySelector('[data-media-card]')?.offsetWidth || this.track.offsetWidth;
          const snappedIndex = Math.round(this.track.scrollLeft / cardWidth);

          if (snappedIndex !== this.currentIndex) {
            this.currentIndex = snappedIndex;
            this.updateState();
          }
        });
      },
      { passive: true },
    );

    // Handle resize
    let resizeTimer;
    let windowWidth = window.innerWidth;

    window.addEventListener(
      'resize',
      () => {
        // Prevent iOS vertical scroll from triggering resize logic (address bar hide/show)
        if (window.innerWidth === windowWidth) return;
        windowWidth = window.innerWidth;

        if (resizeTimer) cancelAnimationFrame(resizeTimer);
        resizeTimer = requestAnimationFrame(() => {
          this.updateVisibility();
          this.updateControlsVisibility();
          this.calculateInitialOffset();
          this.goTo(this.currentIndex);
          this.updateState();
        });
      },
      { passive: true },
    );
  }

  updateVisibility() {
    const w = window.innerWidth;
    this.itemsVisible = w >= DESKTOP_BREAKPOINT ? this.desktopVisible : w >= TABLET_BREAKPOINT ? 2 : 1;
  }

  updateControlsVisibility() {
    if (!this.controls) return;

    const shouldShowControls = window.innerWidth >= DESKTOP_BREAKPOINT;
    this.controls.classList.toggle('hidden', !shouldShowControls);
    this.controls.setAttribute('aria-hidden', shouldShowControls ? 'false' : 'true');
  }

  goTo(index) {
    const children = this.track.querySelectorAll('[data-media-card]');
    if (!children.length) return;

    this.currentIndex = Math.max(0, Math.min(index, this.slideCount - this.itemsVisible));
    const targetChild = children[this.currentIndex];

    if (targetChild) {
      const offset = targetChild.offsetLeft;

      if (window.innerWidth >= DESKTOP_BREAKPOINT) {
        // Subtract initialOffset to keep the first card aligned with the margin
        const moveAmount = offset - this.initialOffset;
        this.track.style.transform = `translateX(-${moveAmount}px)`;
      } else {
        this.track.style.transform = '';
        this.track.scrollLeft = offset;
      }
    }

    this.updateState();
  }

  updateState() {
    this.setButtonState(this.prevBtn, this.currentIndex === 0);
    this.setButtonState(this.nextBtn, this.currentIndex >= this.slideCount - this.itemsVisible);
  }

  setButtonState(btn, isDisabled) {
    if (!btn) return;
    btn.disabled = isDisabled;
    if (isDisabled) {
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.removeAttribute('aria-disabled');
    }
  }
}

function initSections(container = document) {
  const selector = '[data-slider][data-slider-type="content-grid"]:not([data-slider-init])';
  const roots = container.matches?.(selector) ? [container] : container.querySelectorAll(selector);

  roots.forEach((root) => {
    root.dataset.sliderInit = 'true';
    new ContentGrid(root);
  });
}

initSections();

document.addEventListener('shopify:section:load', (e) => initSections(e.target));
